import React, { useEffect, useState, useRef } from 'react';
import { getMoodHistory } from '../api/moodApi';
import { useNavigate } from 'react-router-dom';
import TrackCard from './ui/TrackCard';


const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};

export default function MoodHistory({ isGuest }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMoods, setExpandedMoods] = useState({});
  const navigate = useNavigate();

  // Preview player state
  const initialPreviewState = {
    currentTrackId: null,
    trackTitle: '',
    artistName: '',
    previewUrl: '',
    loading: false,
    error: null
  };

  const [previewState, setPreviewState] = useState(initialPreviewState);
  const previewCacheRef = useRef({});
  const audioRef = useRef(null);
  const [touchedTrackId, setTouchedTrackId] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detect touch device
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);
    return () => window.removeEventListener('resize', checkTouchDevice);
  }, []);

  // Handle preview playback
  const handlePlayPreview = async (track) => {
    const trackKey = track?.id || track?.spotifyId || `${track?.name || ''}-${track?.artist || ''}`;
    
    // If clicking the same track, toggle pause/play
    if (previewState.currentTrackId === trackKey && previewState.previewUrl) {
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
      }
      return;
    }

    // If clicking a different track, stop current and start new
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    const cachedUrl = previewCacheRef.current[trackKey];
    if (cachedUrl) {
      setPreviewState({
        currentTrackId: trackKey,
        trackTitle: track.name,
        artistName: track.artist,
        previewUrl: cachedUrl,
        loading: false,
        error: null
      });
      return;
    }

    setPreviewState({
      currentTrackId: trackKey,
      trackTitle: track.name,
      artistName: track.artist,
      previewUrl: '',
      loading: true,
      error: null
    });

    try {
      const searchTerm = encodeURIComponent(`${track.name} ${track.artist}`.trim());
      const url = `https://itunes.apple.com/search?term=${searchTerm}&entity=song&limit=1`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Preview request failed (${response.status})`);
      }
      const data = await response.json();
      const previewUrl = data?.results?.[0]?.previewUrl || '';

      if (previewUrl) {
        previewCacheRef.current[trackKey] = previewUrl;
        setPreviewState({
          currentTrackId: trackKey,
          trackTitle: track.name,
          artistName: track.artist,
          previewUrl,
          loading: false,
          error: null
        });
      } else {
        setPreviewState({
          currentTrackId: trackKey,
          trackTitle: track.name,
          artistName: track.artist,
          previewUrl: '',
          loading: false,
          error: 'Preview not available'
        });
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
      setPreviewState({
        currentTrackId: trackKey,
        trackTitle: track.name,
        artistName: track.artist,
        previewUrl: '',
        loading: false,
        error: 'Preview unavailable'
      });
    }
  };

  // Handle track touch for mobile
  const handleTrackTouch = (track) => {
    const trackKey = track?.id || track?.spotifyId || `${track?.name || ''}-${track?.artist || ''}`;
    if (touchedTrackId === trackKey) {
      // Second tap - play preview
      handlePlayPreview(track);
      setTouchedTrackId(null);
    } else {
      // First tap - mark as touched
      setTouchedTrackId(trackKey);
      setTimeout(() => setTouchedTrackId(null), 500);
    }
  };

  // Auto-play when preview URL is set
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (previewState.previewUrl) {
      audioEl.load();
      const playPromise = audioEl.play();
      if (playPromise?.catch) {
        playPromise.catch((err) => {
          console.warn('Audio playback prevented:', err);
        });
      }
    } else {
      audioEl.pause();
      audioEl.currentTime = 0;
    }
  }, [previewState.previewUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    if (isGuest) {
      setLoading(false);
      return;
    }

    async function fetchHistory() {
      try {
        const data = await getMoodHistory();
        const sortedData = [...data].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setHistory(sortedData);
        const initialExpanded = {};
        sortedData.forEach((item, index) => {
          const key = item.mood_id || `mood-${index}`;
          initialExpanded[key] = false;
        });
        setExpandedMoods(initialExpanded);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchHistory();
  }, [isGuest]);

  const toggleMood = (key) => {
    setExpandedMoods(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: 'calc(100vh - 60px)',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '2rem 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '3rem 2rem',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          textAlign: 'center',
          color: '#333',
          fontFamily: 'Inter, sans-serif',
          fontSize: '1.2rem',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
        }}>
          ⏳ <strong>Loading your mood history...</strong>
        </div>
      </div>
    );
  }
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  if (isGuest) {
    return (
      <>
        <style>
          {`
            /* Guest History Responsive Styles */
            @media (max-width: 768px) {
              .guest-history-container {
                padding: 1rem 0 !important;
                align-items: flex-start !important;
                padding-top: 8vh !important;
              }
              .guest-history-card {
                max-width: 90vw !important;
                padding: 2rem 1.5rem !important;
                margin: 0 1rem !important;
              }
              .guest-history-title {
                font-size: 1.5rem !important;
              }
              .guest-history-text {
                font-size: 0.95rem !important;
                line-height: 1.4 !important;
              }
            }
            
            @media (max-width: 480px) {
              .guest-history-container {
                padding: 0.5rem 0 !important;
                padding-top: 6vh !important;
              }
              .guest-history-card {
                max-width: 95vw !important;
                padding: 1.5rem 1rem !important;
                margin: 0 0.5rem !important;
              }
              .guest-history-title {
                font-size: 1.3rem !important;
              }
              .guest-history-text {
                font-size: 0.9rem !important;
                line-height: 1.3 !important;
              }
            }
          `}
        </style>
        <div 
          className="guest-history-container"
          style={{ 
            minHeight: 'calc(100vh - 60px)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '2rem 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div 
            className="guest-history-card"
            style={{ 
              maxWidth: '800px', 
              margin: '0 auto', 
              padding: '3rem 2rem',
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <h2 
              className="guest-history-title"
              style={{ 
                marginTop: 0,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              🔒 History Not Saved
            </h2>
            <p className="guest-history-text">
              You're browsing in guest mode. Log in to save your mood history permanently.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (history.length === 0) {
    return (
      <div style={{ 
        minHeight: 'calc(100vh - 60px)',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '2rem 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '3rem 2rem',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          textAlign: 'center',
          color: '#444',
          fontFamily: 'Inter, sans-serif',
          fontSize: '1.2rem',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
        }}>
         <strong>No mood history yet.</strong><br />
          Try entering a mood to get started!
        </div>
      </div>
    );
  }
  
  return (
    <>
      <style>
        {`
          /* Responsive styles for MoodHistory */
          @media (max-width: 768px) {
            .history-page {
              padding: 1rem 0 !important;
            }
            .history-container {
              max-width: 95% !important;
              padding: 1.5rem 0.75rem !important;
              border-radius: 15px !important;
            }
            .history-title {
              font-size: 1.5rem !important;
              margin-bottom: 1.5rem !important;
            }
            .history-date-header {
              font-size: 1rem !important;
              margin-bottom: 0.8rem !important;
            }
            .history-mood-card {
              margin-bottom: 0.8rem !important;
              border-radius: 10px !important;
            }
            .history-mood-header {
              padding: 0.8rem 1rem !important;
            }
            .history-mood-title {
              font-size: 1rem !important;
            }
            .history-expanded-content {
              padding: 1rem !important;
            }
            .history-track-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 0.8rem !important;
            }
            .history-track-grid > * {
              min-width: 0 !important;
              width: 100% !important;
            }
            .history-track-item {
              padding: 0.6rem !important;
              border-radius: 8px !important;
            }
            .history-track-name {
              font-size: 0.85rem !important;
            }
            .history-track-artist {
              font-size: 0.75rem !important;
            }
          }
          
          @media (max-width: 480px) {
            .history-page {
              padding: 0.5rem 0 !important;
            }
            .history-container {
              max-width: 98% !important;
              padding: 1rem 0.5rem !important;
              border-radius: 12px !important;
            }
            .history-title {
              font-size: 1.3rem !important;
              margin-bottom: 1rem !important;
            }
            .history-date-header {
              font-size: 0.9rem !important;
              margin-bottom: 0.6rem !important;
            }
            .history-mood-card {
              margin-bottom: 0.6rem !important;
              border-radius: 8px !important;
            }
            .history-mood-header {
              padding: 0.6rem 0.8rem !important;
            }
            .history-mood-title {
              font-size: 0.9rem !important;
            }
            .history-expanded-content {
              padding: 0.8rem !important;
            }
            .history-track-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 0.4rem !important;
              padding: 0 0.2rem !important;
            }
            .history-track-grid > * {
              min-width: 0 !important;
              width: 100% !important;
            }
            .history-track-item {
              padding: 0.5rem !important;
              border-radius: 6px !important;
              min-height: 120px !important;
            }
            .history-track-name {
              font-size: 0.8rem !important;
              line-height: 1.2 !important;
              word-wrap: break-word !important;
            }
            .history-track-artist {
              font-size: 0.7rem !important;
              line-height: 1.2 !important;
              word-wrap: break-word !important;
            }
          }
        `}
      </style>
      <div style={{ 
        minHeight: 'calc(100vh - 60px)',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '2rem 0',
      }} className="history-page">
        <div style={{ 
          maxWidth: '900px', 
          margin: '0 auto', 
          padding: '2rem 1rem',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
        }} className="history-container">
          <h1 style={{
            fontSize: '1.8rem',
            fontWeight: 700,
            marginBottom: '2rem',
            textAlign: 'center',
            color: '#000000',
          }} className="history-title">
            Your Mood History
          </h1>
  
      {(() => {
        // Group history by date
        const groupedHistory = history.reduce((groups, item, index) => {
          const date = formatDate(item.created_at);
          if (!groups[date]) {
            groups[date] = [];
          }
          groups[date].push({ ...item, originalIndex: index });
          return groups;
        }, {});

        return Object.entries(groupedHistory).map(([date, items]) => (
          <div key={date} style={{ marginBottom: '2rem' }}>
            {/* Date Header */}
            <h2 style={{
              fontSize: '1.1rem',
              color: '#666',
              fontWeight: '600',
              marginBottom: '1rem',
              paddingLeft: '0.5rem',
              borderLeft: '3px solid #667eea',
            }} className="history-date-header">
              {date}
            </h2>
            
            {/* Mood entries for this date */}
            {items.map(({ user_prompt, mood_id, created_at, tracks, originalIndex }) => {
              const moodKey = mood_id || `mood-${originalIndex}`;
              
              return (
                <div key={moodKey} style={{
                  marginBottom: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s ease',
                }} className="history-mood-card">
                  <div 
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem 1.5rem',
                      cursor: 'pointer',
                      backgroundColor: expandedMoods[moodKey] ? '#f0f4f8' : '#f9fbfc',
                      borderBottom: expandedMoods[moodKey] ? '1px solid #ccc' : '1px solid #eee',
                      transition: 'background-color 0.25s ease',
                    }}
                    className="history-mood-header"
                    onClick={() => toggleMood(moodKey)}
                  >
                    <h3 style={{
                      margin: 0,
                      fontSize: '1.1rem',
                      color: '#333',
                      fontWeight: 600
                    }} className="history-mood-title">
                      {user_prompt || 'No prompt available'}
                    </h3>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      style={{
                        transform: expandedMoods[moodKey] ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <path d="M5 7L10 12L15 7" stroke="#555" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
  
            {expandedMoods[moodKey] && (
              <div style={{ padding: '1.25rem 1.5rem' }} className="history-expanded-content">
                <div style={{
                  display: 'grid',
                  gap: '1rem',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                }} className="history-track-grid">
                  {tracks.map((track, i) => {
                    const trackKey = track?.id || track?.spotifyId || `${track?.name || ''}-${track?.artist || ''}`;
                    const isCurrentTrack = previewState.currentTrackId === trackKey;
                    const isPreviewing = isCurrentTrack && Boolean(previewState.previewUrl);
                    const isLoadingPreview = isCurrentTrack && previewState.loading;
                    const previewError = isCurrentTrack ? previewState.error : null;
                    
                    return (
                      <TrackCard
                        key={`track-${moodKey}-${i}`}
                        track={{
                          ...track,
                          image_url: track.image_url || null
                        }}
                        index={i}
                        onDelete={() => {}} // No delete functionality in history
                        isTouchDevice={isTouchDevice}
                        touchedTrackId={touchedTrackId}
                        onTrackTouch={handleTrackTouch}
                        onPreview={handlePlayPreview}
                        isPreviewing={isPreviewing}
                        isLoadingPreview={isLoadingPreview}
                        previewError={previewError}
                        allowDelete={false}
                      />
                    );
                  })}
                </div>
              </div>
            )}
                </div>
              );
            })}
          </div>
        ));
      })()}
      </div>

      {/* Preview Player */}
      {(previewState.loading || previewState.previewUrl || previewState.error) && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderRadius: '12px',
          padding: '1rem 1.5rem',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          minWidth: '300px',
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem'
          }}>
            <div style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#1f2937'
            }}>
              {previewState.trackTitle || 'Song preview'}
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: '#6b7280'
            }}>
              {previewState.artistName}
            </div>
          </div>
          {previewState.loading ? (
            <div style={{
              fontSize: '0.85rem',
              color: '#6b7280',
              textAlign: 'center',
              padding: '0.5rem 0'
            }}>
              Loading preview...
            </div>
          ) : previewState.error ? (
            <div style={{
              fontSize: '0.85rem',
              color: '#ef4444',
              textAlign: 'center',
              padding: '0.5rem 0'
            }}>
              {previewState.error}
            </div>
          ) : (
            <audio
              key={previewState.previewUrl}
              ref={audioRef}
              controls
              src={previewState.previewUrl}
              style={{
                width: '100%',
                height: '32px'
              }}
              onEnded={() => setPreviewState(initialPreviewState)}
              onError={() => setPreviewState(prev => ({
                ...prev,
                previewUrl: '',
                loading: false,
                error: 'Preview unavailable'
              }))}
            />
          )}
        </div>
      )}
    </div>
    </>
  );
  
}
